import React from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import flowRight from 'lodash-es/flowRight';
import { withStyles } from '@material-ui/core/styles';
import { policyTypes as pTypes } from '@ourbranch/policy-types';

import { Card } from 'core/components/card';
import { Label } from 'core/components/label';
import { Loading } from 'core/components/loading';
import Section from 'core/components/section';
import SortableTable from 'core/components/sortable-table';
import { dateFormatter, getTotalPrice, numberFormatter } from 'core/helpers/formatters';
import { get, policyTypes } from 'core/helpers/lookups';
import { useStore } from 'core/store';
import { getPolicyStatus, PolicyStatus } from 'core/helpers/policy-status';
import { checkAdditionalCoverages } from 'core/helpers/quoter.service';
import styles from './policies.styles';

const getPolicyType = get(policyTypes);

// @TODO use mobx computed property
const getPolicyProducer = (policy) => policy?.offer?.quote?.rep || 'N/A';
const Policies = observer(function Policies({ classes, onClick, id }) {
  const { account } = useStore();
  const {
    policies: { list, loading: policiesLoading, getUnsignedBixConversion }
  } = account;
  const loading = policiesLoading || !list.length;

  const rowColor = ({ status, mode, id, policyType, policyDetails, offer }) => {
    if (status === PolicyStatus.InCancellation) {
      return 'red';
    }

    const bixConversionDocsInfo = getUnsignedBixConversion(id);
    const { canAddEarthquake, canAddFlood } = checkAdditionalCoverages(offer, policyType);

    if (
      !bixConversionDocsInfo?.signedDocUploadedInS3 ||
      ((policyType === pTypes.HABundle || policyType === pTypes.Home) &&
        ((policyDetails?.homeCoverage?.interestedInSeparateWindHail &&
          !policyDetails?.homeCoverage?.externalPolicyId) ||
          (canAddEarthquake && policyDetails?.includeEarthquake && !policyDetails?.earthquakeCoverage?.policyId) ||
          (canAddFlood && policyDetails?.includeFlood && !policyDetails?.floodCoverage?.policyId)))
    ) {
      return 'orange';
    }

    return mode;
  };

  const data = list.map((p) => {
    const status = getPolicyStatus(p);
    return {
      ...p,
      producer: getPolicyProducer(p),
      totalPrice: getTotalPrice(p.premium, p.fees, p.surplusContribution || 0),
      status,
      rowMode: rowColor({
        status,
        mode,
        id: p.id,
        policyType: p.policyType,
        policyDetails: p.policyDetails,
        offer: p.offer
      })
    };
  });
  const mode = 'dark';
  return (
    <Section title="Policies" rightLabel="Total" rightValue={`${loading ? '...' : list.length} policies`}>
      {loading ? (
        <Loading type="secondary" />
      ) : (
        <Card className={classes.tableContainer} type="secondary">
          <SortableTable
            mode={mode}
            dataCy="policies"
            selfContained={false}
            headers={[
              ['policyType', 'Policy'],
              ['effectiveDate', 'Start Date'],
              ['totalPrice', 'Total Price'],
              ['producer', 'Producer'],
              ['status', 'Status']
            ]}
            data={data}
            onClick={onClick}
            formatters={{
              policyType: (p, row) => (
                <>
                  {getPolicyType(p)}
                  <Label type="infoLabel" className={classes.policyLabel}>
                    {row.id}
                  </Label>
                </>
              ),
              effectiveDate: dateFormatter,
              endDate: dateFormatter,
              coverage: (c) => (String(c).includes('/') ? c : numberFormatter(c)),
              totalPrice: (p) => `$${p.toFixed(2)}`
            }}
          />
        </Card>
      )}
    </Section>
  );
});

Policies.propTypes = {
  classes: PropTypes.object.isRequired,
  onClick: PropTypes.func.isRequired,
  id: PropTypes.string.isRequired
};

export default flowRight(withStyles(styles))(Policies);
